/**
 * OfflineIndicator Component
 * Shows a banner when the user is offline
 * Also indicates when viewing cached data or has pending syncs
 */

import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CheckCircle, Clock, CloudOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getSyncStats, syncAll } from '@/services/offlineQueue';

export function OfflineIndicator({ className }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setShowReconnected(true);
      // Auto-sync when coming back online
      setIsSyncing(true);
      try {
        await syncAll();
        await loadPendingCount();
      } catch (e) {
        console.error('Auto-sync failed:', e);
      }
      setIsSyncing(false);
      // Hide reconnected message after 3 seconds
      setTimeout(() => setShowReconnected(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load pending count on mount
    loadPendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadPendingCount = async () => {
    try {
      const stats = await getSyncStats();
      setPendingCount(stats.total);
    } catch (e) {
      // IndexedDB might not be available
    }
  };

  const handleManualSync = async () => {
    if (isSyncing || !navigator.onLine) return;
    setIsSyncing(true);
    try {
      await syncAll();
      await loadPendingCount();
    } catch (e) {
      console.error('Manual sync failed:', e);
    }
    setIsSyncing(false);
  };

  // Don't render anything if online, no reconnected message, and no pending items
  if (isOnline && !showReconnected && pendingCount === 0) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] transition-all duration-300",
        className
      )}
    >
      {!isOnline ? (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOff className="h-4 w-4" />
          <span>You're offline. Changes will sync when reconnected.</span>
          {pendingCount > 0 && (
            <span className="bg-amber-600 text-white px-2 py-0.5 rounded-full text-xs">
              {pendingCount} pending
            </span>
          )}
        </div>
      ) : showReconnected ? (
        <div className="bg-green-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium animate-in slide-in-from-top duration-300">
          <CheckCircle className="h-4 w-4" />
          <span>Back online! {isSyncing ? 'Syncing...' : 'Your data has been synced.'}</span>
          {isSyncing && <RefreshCw className="h-4 w-4 animate-spin" />}
        </div>
      ) : pendingCount > 0 && isOnline ? (
        <div className="bg-blue-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4" />
          <span>{pendingCount} item{pendingCount !== 1 ? 's' : ''} waiting to sync</span>
          <button
            onClick={handleManualSync}
            disabled={isSyncing}
            className="ml-2 px-2 py-1 bg-white/20 rounded hover:bg-white/30 transition-colors flex items-center gap-1"
          >
            <RefreshCw className={cn("h-3 w-3", isSyncing && "animate-spin")} />
            {isSyncing ? 'Syncing...' : 'Sync now'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * CachedDataBadge Component
 * Shows when data is from cache
 */
export function CachedDataBadge({ cachedAt, className }) {
  if (!cachedAt) return null;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-full",
        "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400",
        "text-xs font-medium",
        className
      )}
    >
      <CloudOff className="h-3 w-3" />
      <span>Viewing cached data</span>
    </div>
  );
}

/**
 * PendingSyncBadge Component
 * Shows count of items pending sync
 */
export function PendingSyncBadge({ count, className }) {
  if (!count || count === 0) return null;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-1 rounded-full",
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
        "text-xs font-medium",
        className
      )}
    >
      <Clock className="h-3 w-3" />
      <span>{count} pending sync</span>
    </div>
  );
}

/**
 * useOnlineStatus Hook
 * Returns current online status and syncing state
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastOnline, setLastOnline] = useState(Date.now());

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnline(Date.now());
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, lastOnline };
}

export default OfflineIndicator;
